import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createUpdateReducer } from '@ff-os/pdp-shared-redux/createUpdateReducer';
import { PdpState } from '@ff-os/pdp-shared-redux/store';
import { selectGroupById } from './groupsSlice';

// Selectors
export const selectIsPhotoFunProcessing = (state: PdpState) => state.ui.general.isPhotoFunProcessing;
export const selectCanHaveBorderDraggedInto = (state: PdpState) => state.ui.general.canHaveBorderDraggedInto;
export const selectIsVariantCollapsableOpen = (state: PdpState) => state.ui.general.isVariantCollapsableOpen;
export const selectHasFotofunAutoOpenedThisSession = (state: PdpState) => state.ui.general.hasFotofunAutoOpenedThisSession;
export const selectIsFotofunPanelOpen = (state: PdpState) => state.ui.general.isFotofunPanelOpen;

export const selectHasMagicBorderApplied = (state: PdpState, groupId: string): boolean => {
    const group = selectGroupById(state as any, groupId);
    return !!group?.magicBorderId || !!group?.patternId;
};

export const selectMagicBorderAutoAppliedTimestamp = (state: PdpState, groupId: string): number | undefined => {
    return state.ui.general.magicBorderAutoAppliedByGroup?.[groupId];
};

export type FotoFunTab = 'main' | 'magical' | 'standard' | 'accessories';

type Ui = {
    showProjectIncompatible: boolean;
    isPhotoPrintsWindowOpen: boolean;
    isFotofunPanelOpen: boolean;
    isFotofunBannerHidden: boolean;
    fotoFunTab: FotoFunTab;
    isPhotoFunProcessing?: boolean;
    canHaveBorderDraggedInto?: boolean;
    hasMagicBorderApplied?: boolean;
    magicBorderByGroup?: Record<string, string>;
    isVariantCollapsableOpen?: boolean;
    frameIdMobile?: string;
    accessoryIdMobile?: string;
    frameIsDragging: boolean;
    accessoryIsDragging: boolean;
    draggedAccessory?: string;
    magicBorderAutoAppliedByGroup?: Record<string, number>; // timestamp of when auto-applied
    hasFotofunAutoOpenedThisSession?: boolean;
};

export const { actions, reducer } = createSlice({
    name: 'general',
    initialState: {
        showProjectIncompatible: false,
        isPhotoPrintsWindowOpen: false,
        isFotofunPanelOpen: false,
        isFotofunBannerHidden: false,
        fotoFunTab: 'main' as FotoFunTab,
        isPhotoFunProcessing: false,
        canHaveBorderDraggedInto: false,
        hasMagicBorderApplied: false,
        magicBorderByGroup: {},
        isVariantCollapsableOpen: true,
        frameIdMobile: "",
        accessoryIdMobile: "",
        frameIsDragging: false,
        accessoryIsDragging: false,
        draggedAccessory: "",
        magicBorderAutoAppliedByGroup: {},
        hasFotofunAutoOpenedThisSession: false
    } as Ui,
    reducers: {

        overwrite: (state, action: PayloadAction<any>) => {
            return action.payload;
        },
        updateItem: createUpdateReducer<Ui>(),

        removeMagicBorderForGroup: (state, action: PayloadAction<string>) => {
            if (!state.magicBorderByGroup) {
                return;
            }

            delete (state.magicBorderByGroup as Record<string, string>)[action.payload];
        },

        updateMagicBorderForGroup: (state, action: PayloadAction<{ groupId: string; magicBorderId: string }>) => {

            if (!state.magicBorderByGroup) {
                state.magicBorderByGroup = {};
            }

            (state.magicBorderByGroup as Record<string, string>)[action.payload.groupId] = action.payload.magicBorderId;
        },

        triggerMagicBorderAutoApplied: (state, action: PayloadAction<string>) => {
            if (!state.magicBorderAutoAppliedByGroup) {
                state.magicBorderAutoAppliedByGroup = {};
            }

            (state.magicBorderAutoAppliedByGroup as Record<string, number>)[action.payload] = Date.now();
        },

        markFotofunAutoOpenedThisSession: (state) => {
            state.hasFotofunAutoOpenedThisSession = true;
        }
    }
});

export const createUiBindings = () => ({

    createSelector: <T>(selector: (state: Ui) => T) => createSelector(
        (state: PdpState) => state.ui.general as Ui,
        o => selector(o)),

    createUpdateAction: <TKey extends keyof Ui>(key: TKey) =>
        (value: Ui[TKey]) => actions.updateItem({ key, value })
});